// /expat-relocation — TH hub for Housing, Banking, Schools, Healthcare. Phase P-EXPAT-RELOCATION.
import { createFileRoute, Link } from "@tanstack/react-router";
import type { ExpatTopic, ExpatCategory } from "@/data/expat-relocation";
import { EXPAT_TOPICS_LITE, EXPAT_LITE_BY_CATEGORY, EXPAT_CAT_LABELS_TH, EXPAT_CAT_LABELS_EN } from "@/data/expat-index.gen";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, faqPageSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { Home, Phone, MessageCircle, ArrowRight, Sparkles, ShieldCheck, Building2, Wallet, GraduationCap, HeartPulse } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";
const PATH = "/expat-relocation";
const TITLE = "ย้ายมาอยู่ไทย — ที่พัก ธนาคาร โรงเรียน สุขภาพ คู่มือคนต่างชาติ 2026 | NYC";
const DESC =
  "คู่มือย้ายมาอยู่ประเทศไทยสำหรับคนต่างชาติฉบับสมบูรณ์: เช่า/ซื้อคอนโด, BOI 1 ไร่, FET form, เปิดบัญชี Kasikorn/SCB, Wise/SWIFT, mortgage — ปรึกษาฟรีก่อนยื่นจริง ลดโอกาสเอกสารตีกลับ";

export const Route = createFileRoute("/expat-relocation/")({
  head: () => {
    const url = `${SITE}${PATH}`;
    return {
      meta: [
        { title: ctaTitle(TITLE, "/expat-relocation", "th")}, { name: "description", content: ctaDesc(DESC, "/expat-relocation", "th")},
        { property: "og:title", content: ctaTitle(TITLE, "/expat-relocation", "th")}, { property: "og:description", content: ctaDesc(DESC, "/expat-relocation", "th")},
        { property: "og:url", content: url }, { property: "og:locale", content: "th_TH" },
        { name: "keywords", content: "ย้ายมาอยู่ไทย, คู่มือ expat, เช่าคอนโดต่างชาติ, ซื้อคอนโด foreigner quota, เปิดบัญชีธนาคารต่างชาติ, BOI 1 ไร่, โรงเรียนนานาชาติ, IB Bangkok, Bumrungrad, Samitivej, Non-MT visa, ประกันสังคมต่างชาติ, Worldwide Income 2024" },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks(PATH, "/en/expat-relocation")],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "CollectionPage", "@id": `${url}#page`,
              name: TITLE, description: DESC, url, inLanguage: "th-TH",
              mainEntity: {
                "@type": "ItemList", numberOfItems: EXPAT_TOPICS_LITE.length,
                itemListElement: EXPAT_TOPICS_LITE.map((t, i) => ({
                  "@type": "ListItem", position: i + 1, name: t.titleTh,
                  url: `${SITE}/expat-relocation/${t.slug}`,
                })),
              },
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: "Thailand Expat Relocation Concierge",
              serviceType: "Expat Relocation, Housing, Banking, Schools, Healthcare Setup",
              provider: { "@type": "LegalService", name: "NYC Legal & Notary Service", telephone: "+66922429293", url: SITE },
              areaServed: "TH",
              offers: { "@type": "Offer", price: "8000", priceCurrency: "THB", description: "Full relocation concierge package" },
            }),
          },
          faqPageSchema([
            { q: "ย้ายมาอยู่ไทยต้องเตรียมอะไรบ้าง?", a: "5 ขั้นตอนหลัก: (1) วีซ่า Non-Immigrant ที่เหมาะ (Non-B / LTR / DTV / Retirement / Marriage) (2) ที่พัก (เช่า 8K-80K/เดือน หรือ ซื้อคอนโด foreign quota) (3) เปิดบัญชีธนาคาร (Kasikorn/SCB/Bangkok Bank) (4) โรงเรียน (ถ้ามีลูก — international 200K-1.2M/ปี) (5) ประกันสุขภาพ (local 15K-50K/ปี หรือ international 60K-300K/ปี)" },
            { q: "คนต่างชาติเช่ากับซื้อคอนโดเลือกอะไร?", a: "ระยะสั้น <2 ปี = เช่า (สัญญา ≤3 ปี ตาม ป.พ.พ. ม.538) / 2–5 ปี = เช่า + วางแผน DTV/LTR / 5+ ปี งบ ≥5 ล้านบาท = ซื้อใน Foreign Quota 49% ตาม พ.ร.บ.อาคารชุด 2522 ม.19 ทวิ ต้องโอนเงินจากต่างประเทศพร้อมใบ FET (ธปท. ระเบียบ ปต. 7/2562) และยื่น ภ.ง.ด. ค่าเช่า/ภาษีธุรกิจเฉพาะ 3.3% เมื่อขายภายใน 5 ปี" },
            { q: "ธนาคารไหนเปิดบัญชีง่ายสุดสำหรับ expat?", a: "Kasikorn (K Plus app ดี), Bangkok Bank (ใหญ่สุด, สาขามาก), SCB (Easy app) — ทุกแห่ง require Non-Immigrant visa + work permit หรือ Certificate of Residence" },
            { q: "International school ในไทยมีกี่แห่ง?", a: "180+ แห่ง (Bangkok 130, Phuket 8, Chiang Mai 6, อื่น ๆ 40+) — IB / British / American / Singaporean / Australian curriculum / ค่าเทอม 200K-1.2M บาท/ปี" },
            { q: "ประกันสุขภาพ expat เลือกแบบไหน?", a: "Local plan (Pacific Cross, AXA Thailand) 15K-50K/ปี cover รพ. เครือข่ายกลาง / International plan (Cigna, BUPA, Allianz) 60K-300K/ปี cover Bumrungrad/Samitivej + worldwide emergency / LTR visa ต้องมี ≥ USD 50,000 coverage" },
            { q: "ฉุกเฉินในไทยโทรเบอร์อะไร?", a: "1669 (medical emergency, รถพยาบาลฟรี) / 1155 Tourist Police (English/Chinese/Russian) / 191 ตำรวจ / 199 ดับเพลิง+กู้ภัย / private hospital: Bumrungrad 02 011 5222, Bangkok Hospital 1719, Samitivej 02 022 2222" },
          ]),
        ]),
      ],
    };
  },
  component: ExpatHubTh,
});

const CAT_ICONS: Record<ExpatCategory, typeof Building2> = {
  housing: Building2, banking: Wallet, schools: GraduationCap, healthcare: HeartPulse,
};

function ExpatHubTh() {
  return (
    <main className="min-h-screen bg-background">
      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-16 pb-12">
        <div className="mx-auto max-w-6xl">
          <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-primary">หน้าแรก</Link>
            <span className="mx-2">/</span><span>Expat Relocation</span>
          </nav>
          <span className="inline-block rounded-full bg-primary/15 px-3 py-1 text-xs font-bold text-primary">
            <Home className="mr-1 inline h-3 w-3" /> Expat Concierge · {EXPAT_TOPICS_LITE.length} หัวข้อ · อัปเดต 2026
          </span>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">ย้ายมาอยู่ไทย — คู่มือคนต่างชาติฉบับสมบูรณ์</h1>
          <p className="mt-4 max-w-3xl text-lg text-muted-foreground">
            ที่พักอาศัย · ธนาคารและการเงิน · โรงเรียนและการศึกษา · สุขภาพและประกัน — รวม 24 หัวข้อ ค่าใช้จ่ายจริง เอกสาร กฎหมาย และขั้นตอนทุกอย่างที่คนต่างชาติต้องรู้ก่อนตัดสินใจย้ายมาประเทศไทย
          </p>
          <div className="quick-answer aeo-speakable mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5" data-speakable="true">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <h2 className="text-base font-bold sm:text-lg">ย้ายมาอยู่ไทย ต้องเตรียม 5 อย่างหลัก</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
              (1) วีซ่าที่เหมาะ — DTV/LTR/Non-B/Non-O Retirement/Marriage — เลือกตามอายุ รายได้ และเป้าหมาย (2) ที่พัก — เช่า 8K-80K/เดือน หรือ ซื้อ Foreign Quota 49% + FET form (3) บัญชีธนาคาร — Kasikorn/SCB/Bangkok Bank + Certificate of Residence (4) โรงเรียน (ถ้ามีลูก) — international 200K-1.2M/ปี, EP/MEP รัฐ 30K-80K/ปี (5) ประกันสุขภาพ — local 15K-50K/ปี หรือ international 60K-300K/ปี (LTR ต้อง ≥ 50,000 USD) NYC จัดการครบทั้ง 5 ขั้นตอนใน package เดียว
            </p>
          </div>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="tel:+66922429293" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              <Phone className="h-4 w-4" /> โทร 092-242-9293
            </a>
            <a href="https://line.me/R/ti/p/@nyc-notary" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <MessageCircle className="h-4 w-4" /> LINE @nyc-notary
            </a>
            <Link to="/visa-wizard" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <Sparkles className="h-4 w-4" /> เริ่ม Visa Wizard
            </Link>
          </div>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl">เลือกหมวดที่ต้องการ</h2>
          <p className="mt-2 text-sm text-muted-foreground">4 หมวดหลัก · 24 หัวข้อ · คลิกเพื่อดูค่าใช้จ่ายจริง เอกสาร และขั้นตอน</p>
          <div className="mt-8 space-y-10">
            {(Object.keys(EXPAT_LITE_BY_CATEGORY) as ExpatCategory[]).map((cat) => {
              const Icon = CAT_ICONS[cat];
              return (
                <div key={cat}>
                  <h3 className="mb-4 flex items-center gap-2 text-lg font-bold text-primary">
                    <Icon className="h-5 w-5" /> {EXPAT_CAT_LABELS_TH[cat]}
                    <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">{EXPAT_LITE_BY_CATEGORY[cat].length} หัวข้อ</span>
                  </h3>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {EXPAT_LITE_BY_CATEGORY[cat].map((t) => (
                      <Link
                        key={t.slug}
                        to="/expat-relocation/$topic"
                        params={{ topic: t.slug }}
                        className="group rounded-2xl border border-border bg-card p-4 transition hover:-translate-y-0.5 hover:border-primary hover:shadow-md"
                        data-track-zone="expat-hub-th"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">{t.icon}</span>
                          <div className="font-bold text-sm">{t.titleTh}</div>
                        </div>
                        <div className="mt-2 line-clamp-2 text-xs text-muted-foreground">{t.taglineTh}</div>
                        <div className="mt-3 flex items-center justify-between text-xs">
                          <span className="rounded-full bg-muted px-2 py-0.5 font-semibold text-muted-foreground">{t.costTh.split(" ")[0]}</span>
                          <span className="inline-flex items-center gap-1 font-semibold text-primary">ดู <ArrowRight className="h-3 w-3" /></span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-muted/30 px-4 py-12">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl"><ShieldCheck className="mr-2 inline h-6 w-6 text-primary" />บริการที่เกี่ยวข้อง</h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { t: "Visa Wizard — เลือกวีซ่าใน 60 วินาที", d: "ตอบ 5 คำถาม → 7 visa scoring", to: "/visa-wizard" as const },
              { t: "เปรียบเทียบ 7 ประเภทวีซ่า", d: "Tourist · DTV · LTR · Non-O · Non-B · Non-ED · Marriage", to: "/visa-matrix" as const },
              { t: "DTV 5 ปี Digital Nomad", d: "Workcation · Freelancer · 9 ประเภท", to: "/dtv" as const },
              { t: "LTR 10 ปี HSP/WFTP", d: "Work from Thailand Professional", to: "/ltr" as const },
              { t: "Compliance — TM30 / 90-day", d: "Re-entry / Overstay / TDAC", to: "/visa-compliance" as const },
              { t: "Company Setup", d: "BOI / Treaty of Amity / Thai Ltd", to: "/company-setup" as const },
              { t: "Work Permit + Non-B", d: "15 สัญชาติ BOI ฟาสต์แทร็ก", to: "/work-permit" as const },
              { t: "แลกใบขับขี่", d: "12 สัญชาติหลัก", to: "/driving-license" as const },
              { t: "แปลเอกสาร + Apostille", d: "60+ ประเทศ Hague", to: "/translate" as const },
            ].map((s) => (
              <Link key={s.t} to={s.to} className="rounded-xl border border-border bg-card p-4 hover:border-primary">
                <div className="font-bold">{s.t}</div>
                <div className="mt-1 text-xs text-muted-foreground">{s.d}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <TrustLayer variant="compact" />
    </main>
  );
}
